import { Injectable } from '@angular/core';
import { getBrowserQueryString, replaceBrowserUrlState } from '@app/functions/browser-query-string';
import { makeDropdownOption } from '@app/functions/make-dropdown-option';
import { deepClone } from '@app/functions/deep-clone';
import { GlobalSearchStatusEnum } from '@app/models/enums/global-search-status.enum';
import { ProductSubtypeEnum } from '@app/models/enums/product-subtype.enum';
import { ProductTypeEnum } from '@app/models/enums/product-type.enum';
import { SearchStatusEnum } from '@app/models/enums/search-status.enum';
import { AssayCatalog } from '@app/models/interfaces/assay-catalog.interface';
import { Dropdown } from '@app/models/interfaces/Dropdown';
import { ChangeFilterParams, IModalFilters, INavigator, ISearchFilters, IStoreService, ITempFilters } from '@app/models/interfaces/ISearchFilters';
import { ResultCountInterface } from '@app/models/interfaces/result-count.interface';
import { SearchCountsInterface } from '@app/models/interfaces/search-counts.interface';
import { SearchParamsInterface } from '@app/models/interfaces/search-params.interface';
import { SearchResultsInterface } from '@app/models/interfaces/search-results.interface';
import { TaqmanProductInterface } from '@app/models/interfaces/taqman-product.interface';
import { FilterSearchRequestInterface } from '@app/models/requests/filter-search-request.interface';
import { TaqmanProductCountRequestInterface, TaqmanSearchRequestInterface } from '@app/models/requests/taqman-search-request.interface';
import { TaqmanGlobalSearchResponseInterface } from '@app/models/responses/taqman-search-response.interface';
import { SelectItem } from '@app/modules/tf-filters';
import { TfNotificationsService } from '@app/modules/tf-notifications';
import { GXProductInterface } from '@app/products/ge/arrays/models/interfaces/gx-product.interface';
import { GEProductInterface } from '@app/products/ge/assays/models/interfaces/ge-product.interface';
import { MicrornaArrayProductInterface } from '@app/products/microrna/microrna-array/models/interfaces/microrna-array-product.interface';
import { SNPProductInterface } from '@app/products/snp/models/interfaces/snp-product.interface';
import { TfApiService } from '@app/services/api.service';
import { SearchService } from '@app/services/search.service';
import { BehaviorSubject, Observable } from 'rxjs';

const initialSearchParams: SearchParamsInterface = {
    filters: '',
    keyword: '',
    productType: null,
    productSubtype: null,
    sortOrder: '',
    sortBy: '',
    limit: '25',
    offset: '0',
}

const initialFilters: ITempFilters = {
    filters: [],
    filterState: [],
    selectedOptions: [],
    geneFilterKeyword: [],
    overlayVisible: [],
    isFilterPillOpened: false
}

const modalFilters: IModalFilters = {
    filters: [],
    totalResultsCount: 0
}

const initialProductCountParams: TaqmanProductCountRequestInterface = {
    keyword: '*',
    productType: null,
    productSubType: null,
    species: ''
}

const initialSearchResultCounts: SearchCountsInterface = {
    assayCount: 0,
    arrayCount: 0
}

const searchKeywordRegex = /^[0-9a-zA-Z\-_.,:;()]+$/;

const DYE_ORDER_CONFIG = {
    GE_ASSAYS: [
        ["FAM (900 nM Primer)", "FAM-MGB"],
        ["FAM PL (350 nM Primer Limited)", "FAM-MGB_PL", "FAM-MGB_Primer Limited"],
        ["VIC (900 nM Primer)", "VIC-MGB"],
        ["VIC PL (150 nM Primer Limited)", "VIC-MGB_PL", "VIC-MGB_Primer Limited"],
        ["ABY PL (350 nM Primer Limited)", "ABY-MGB_PL", "ABY-MGB_Primer Limited"],
        ["JUN PL (350 nM Primer Limited)", "JUN-MGB_PL", "JUN-MGB_Primer Limited"],
        ["CYN5 PL (350 nM Primer Limited)", "CYN5-MGB_PL", "CYN5-MGB_Primer Limited", "CY5-MGB_PL", "CY5-MGB_Primer Limited"]
    ] as string[][]
};

@Injectable({
    providedIn: 'root'
})
export class StoreService {

    private _tempFilters$ = new BehaviorSubject<ITempFilters>(initialFilters);
    private _modalFilters$ = new BehaviorSubject<IModalFilters>(modalFilters);

    tempFilters$: Observable<ITempFilters> = this._tempFilters$;
    modalFilters$: Observable<IModalFilters> = this._modalFilters$;

    searchStatus$ = new BehaviorSubject<SearchStatusEnum>(SearchStatusEnum.initial);
    searchParams$ = new BehaviorSubject<SearchParamsInterface>(initialSearchParams);
    searchResults$ = new BehaviorSubject<SearchResultsInterface>(null);

    private _searchResultCountsState$ = new BehaviorSubject<SearchCountsInterface>(initialSearchResultCounts);
    public searchResultCountsState$: Observable<SearchCountsInterface> = this._searchResultCountsState$;

    selectedResults$ = new BehaviorSubject<any>(null);
    allSelectedResults$ = new BehaviorSubject<boolean>(false);

    private _globalSearchResults$ = new BehaviorSubject<TaqmanGlobalSearchResponseInterface>(null);
    get globalSearchResults$(): Observable<TaqmanGlobalSearchResponseInterface> {
        return this._globalSearchResults$;
    }

    private _globalSearchStatus$ = new BehaviorSubject<GlobalSearchStatusEnum>(GlobalSearchStatusEnum.initial);
    get globalSearchStatus$(): Observable<GlobalSearchStatusEnum> {
        return this._globalSearchStatus$;
    }


    limitWarning = 4;
    warningShown: boolean = false;
    firstShown: boolean = false;
    firstFiltersLoaded: boolean = true;
    initialGeneFilters: any[] = [];
    private deepLinkProductLoadInProgress: boolean = false;

    constructor(
        private apiService: TfApiService,
        private tfNotificationsService: TfNotificationsService,
        private searchService: SearchService
    ) {
    }

    setSelectedFilters(nextState: SelectItem): void {
        const currentFilters = [...this.getFilters()];
        const newCurrentFilters = this._modifiedCurrentFilter(currentFilters, nextState);

        this.setLocalFilters({ filters: newCurrentFilters });
        this.changeFilterState(nextState);
    }

    _modifiedCurrentFilter(currentFilters: SelectItem[], nextState: SelectItem) {
        const mergedFilters = [...currentFilters, nextState];
        const hashMap = new Map();
        const newCurrentFilters = []

        for (const filter of mergedFilters) {
            const key = `${filter['code']}~${this.normalizeSelectedFilterValue(filter['app'])}`;

            if (
                filter['code'] === nextState['code']
                && this.normalizeSelectedFilterValue(filter['app']) === this.normalizeSelectedFilterValue(nextState['app'])
                && nextState['isChecked'] === false
            ) {
                hashMap.delete(key);
                continue;
            }
            hashMap.set(key, filter);
        }

        for (const [_, value] of hashMap.entries()) {
            newCurrentFilters.push(value)
        }

        return newCurrentFilters;
    }

    private normalizeSelectedFilterValue(value: string): string {
        return (value || '').replace(/\s*\[[^\]]+\]\s*$/, '').trim().toLowerCase();
    }

    changeFilterState(clickedOption: SelectItem) {
        const filterState = this.getLocalFilters('filterState');

        filterState.forEach((filterState: ISearchFilters) => {
            filterState.navigators.forEach((navigator: INavigator) => {
                if (navigator.solrFacetName === clickedOption['code'] && navigator.name === clickedOption.value.code) {
                    navigator.checked = clickedOption['isChecked'];
                }
            })
        })

        this.setLocalFilters({ filterState });
    }

    removeTempFilter(nextState: SelectItem): void {
        const oldFilters = [...this.getFilters()];
        const filteredFilters = oldFilters.filter(filter => filter['code'] && nextState['code'] && filter['app'] !== nextState['app']);

        this.setLocalFilters({ filters: filteredFilters });
        this.changeSelectedOptions(nextState);
        this.changeFilterState({ ...nextState, isChecked: false });
        this.setFilters();
    }

    changeSelectedOptions(nextState: SelectItem) {
        const selectedOptions = this.getLocalFilters('selectedOptions');

        for (let i = 0; i < selectedOptions.length; i++) {
            selectedOptions[i].forEach((selectedOption, index) => {
                if (selectedOption['code'] === nextState['code'] && selectedOption.app === nextState['app']) {
                    selectedOptions[i].splice(index, 1);
                }
            })
        }

        this.setLocalFilters({ selectedOptions: selectedOptions });
    }

    resetTempFilters(): void {
        this.setLocalFilters({ filters: [], selectedOptions: [], filterState: [] })
        this.setFilters();
    }

    setFilters(filterIndex?: number, nextState?: ChangeFilterParams): void {
        if (nextState?.clickedOption) this.setSelectedFilters(nextState.clickedOption);
        const newFilters = this._manipulateSelectedFilters();
        const strFilters = this._getFilteredString(newFilters);

        this.changeSearchParams({
            filters: strFilters,
            offset: '0',
            limit: parseInt(this.searchParams$.getValue().limit) < 25 ? '25' : this.searchParams$.getValue().limit
        });
        this.doSearch();
        this.setFilterPillState();

        if (nextState) {
            const selectedOptions = this.getLocalFilters('selectedOptions');
            nextState.selectedOptions.forEach(selectedOption => {
                selectedOption['isChecked'] = true;
            })
            selectedOptions[filterIndex] = nextState.selectedOptions;

            this.setLocalFilters({ selectedOptions: selectedOptions });
        }
    }

    setSelectedOptions(filterIndex: number, nextState: SelectItem[]) {
        if (nextState) {
            const selectedOptions = this.getLocalFilters('selectedOptions');
            selectedOptions[filterIndex] = nextState;

            this.setLocalFilters({ selectedOptions: selectedOptions });
        }
    }

    setSearchValue(searchValue: string) {
        this.searchParams$.next({ ...this.searchParams$.getValue(), filters: '', keyword: searchValue, offset: '0' });
    }

    setFilterState(filterState: Array<ISearchFilters>): void {
        this.setLocalFilters({ filterState })
    }

    setDefaultState(filterLength: number): void {
        let emptySelectedOptions = new Array(filterLength);
        emptySelectedOptions = Array.from(emptySelectedOptions, () => []);

        this.setLocalFilters({ selectedOptions: emptySelectedOptions });
    }

    changeSearchType(nextState: IStoreService): void {
        this.searchParams$.next({
            ...this.searchParams$.getValue(),
            keyword: nextState.keyword,
            filters: '',
            offset: '0',
            productType: nextState.productType,
            productSubtype: nextState.productSubtype
        })
        this.setFilterPillState();
        this.resetFilters();
    }

    changeSearchParams(searchParams: SearchParamsInterface): void {
        this.searchParams$.next({ ...this.searchParams$.getValue(), ...searchParams });
    }

    doSearchProducts(): void {

        this.searchStatus$.next(SearchStatusEnum.searching);

        this.resetSearchResults();

        const searchParams: SearchParamsInterface = this.searchParams$.getValue();
        const filterState: ISearchFilters[] = this.getLocalFilters('filterState');

        if (searchKeywordRegex.test(searchParams.keyword) == false && searchParams.keyword != '*') {
            searchParams.keyword = searchParams.keyword.replace(/[^0-9a-zA-Z\-_.,:;()\s]/g, '');
        }

        if (searchParams.filters && (!filterState || filterState.length === 0)) {
            const unfilteredSearchParams: SearchParamsInterface = {
                ...searchParams,
                filters: '',
                // Build facet state from the same result scope as the filtered
                // search. This keeps every filter limited to relevant values
                // when the page is loaded or refreshed.
                broadSearch: false
            };

            this.searchService.doSearch(unfilteredSearchParams, []).subscribe({
                next: (initialResults) => {
                    // Keep the complete facet metadata independent from the
                    // filtered request. The API/client filter handling can
                    // mutate navigator objects (for example checked state),
                    // so sharing this array causes the second response to
                    // replace the full cross-reactivity list.
                    const initialFilterState = deepClone(initialResults.filters || []) as ISearchFilters[];

                    if (this.firstFiltersLoaded) {
                        this.initialGeneFilters = initialFilterState.filter(filter => filter.name === 'Genes')[0] as any;
                    }

                    this.firstFiltersLoaded = false;
                    this.setFilterState(initialFilterState);

                    if (searchParams.filters) {
                        const browserFilters = searchParams.filters;
                        searchParams.filters = this.decodeFiltersUrlParam(searchParams.filters, initialFilterState);
                        this.checkRemoveCrossReactivityNavigator(browserFilters, initialFilterState);
                    }

                    this.searchService.doSearch(searchParams, initialFilterState).subscribe({
                        // The filtered response contains the facet values
                        // relevant to the current result set. Do not replace
                        // it with the preliminary unfiltered facet state.
                        next: (results) => this.processSearchResults(searchParams, initialFilterState, results),
                        error: () => {
                            this.searchStatus$.next(SearchStatusEnum.error);
                        }
                    });
                },
                error: () => {
                    this.searchStatus$.next(SearchStatusEnum.error);
                }
            });

            return;
        }

        this.searchService.doSearch(searchParams, filterState).subscribe({
            next: (results) => this.processSearchResults(searchParams, filterState, results),
            error: () => {
                this.searchStatus$.next(SearchStatusEnum.error);
            }
        });

    }

    private processSearchResults(
        searchParams: SearchParamsInterface,
        filterState: ISearchFilters[],
        results: SearchResultsInterface,
        preserveFilterState = false,
    ): void {
        if (this.firstFiltersLoaded) {
            this.initialGeneFilters = results.filters.filter(filter => filter.name === 'Genes')[0];
        }

        this.firstFiltersLoaded = false;

        this.updateResultCount({
            productType: searchParams.productType,
            productSubType: searchParams.productSubtype,
            count: results.totalHits
        })

        const searchResults = preserveFilterState && filterState?.length
            ? { ...results, filters: filterState }
            : results;

        this.searchResults$.next(searchResults);

        this.setFilterState(searchResults.filters);

        if (results.totalHits > 0) {

            if (results.suggestedKeywords && results.suggestedKeywords.length > 0) {
                this.searchStatus$.next(SearchStatusEnum.doneWithSuggestions)
            } else {
                this.searchStatus$.next(SearchStatusEnum.doneWithResults);
                this.unselectAllProducts();
            }

        } else if (results.totalHits === 0 && filterState && filterState.length > 0) {
            this.searchStatus$.next(SearchStatusEnum.doneWithNoResultsFilters);
        } else {
            this.searchStatus$.next(SearchStatusEnum.doneWithNoResults);
        }

        this.firstFiltersLoaded = false;
    }

    doSearchProductCounts(): void {

        this._globalSearchStatus$.next(GlobalSearchStatusEnum.searching);

        const searchParams = this.searchParams$.getValue();
        const productTypeParsed = searchParams.productType && searchParams.productType.toLowerCase() in ['ge', 'gx'] ? 'ge' : null;

        if (searchKeywordRegex.test(searchParams.keyword) == false  && searchParams.keyword != '*') {
            searchParams.keyword = searchParams.keyword.replace(/[^0-9a-zA-Z\-_.,:;()\s]/g, '');
        }

        const taqmanProductCountParams: TaqmanProductCountRequestInterface = {
            keyword: searchParams.keyword,
            productType: ProductTypeEnum[productTypeParsed]
        };

        this.apiService.searchProductCounts(taqmanProductCountParams).subscribe({
                next: (response) => {
                    const newResults: TaqmanGlobalSearchResponseInterface = {
                        client: response.client,
                        countryCode: response.countryCode,
                        detectedProductSubType: response.detectedProductSubType,
                        exactMatch: response.exactMatch,
                        fastQueryTime: response.fastQueryTime,
                        geneData: response.geneData,
                        keyword: searchParams.keyword,
                        productTypeHits: response.productTypeHits,
                        productTypes: response.productTypes,
                        querySuccess: response.querySuccess,
                        taqManSearch: response.taqManSearch,
                        totalTime: response.totalTime,
                        species: response.species,
                        suggestedKeywords: response.suggestedKeywords,
                        keywordFound: response.keywordFound,
                        suggestedKeywordsArray: this.parseSuggestedKeywords(response.suggestedKeywords),
                        keywordUsed: response.keyword
                    };

                    this._globalSearchResults$.next(newResults);

                    if (response.keywordFound === true) {
                        const validatedType = searchParams.productType ? searchParams.productType : searchParams.productSearch ? searchParams.productSearch : null;
                        this.validateResults(response, validatedType);
                    } else if (response.keywordFound === false && response.suggestedKeywords && response.keywordUsed !== response.keyword && response.suggestedKeywords.length > 0) {
                        this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithTypo);
                    } else if (response.keywordFound === false && (response.suggestedKeywords === null || response.suggestedKeywords.length === 0)) {
                        this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithZeroResults);
                    }
                },
                error: (err) => {
                    console.log(err);
                    this.tfNotificationsService.showErrorHtml('There has been an error, please try again later.', 'Error',
                        { toastClass: 'availability-notification', timeOut: 10000, extendedTimeOut: 10000 }
                    );
                    this._globalSearchStatus$.next(GlobalSearchStatusEnum.error);
                }
            }
        );
    }

    validateResults(response: TaqmanGlobalSearchResponseInterface, productType: string): void {
        if (response.keywordFound === true && response.productTypeHits) {
            if (productType && productType.toLowerCase() !== 'apps') {
                let parsedProductType = [];
                switch (productType) {
                    case 'ge' :
                        parsedProductType = ['TAQMAN_GE'];
                        break;
                    case 'gx' :
                        parsedProductType = ['ARRAY_GX'];
                        break;
                    case 'microrna' :
                        parsedProductType = ['ADVANCED_MIRNA', 'MIRNA_CONTROL_ASSAY', 'PRI_MIRNA', 'ARRAY_MIRNA'];
                        break;
                    case 'mutation' :
                        parsedProductType = ['DPCR', 'CPCR'];
                        break;
                    case 'microbe' :
                        parsedProductType = ['MICROBE_DETECTION', 'MICROBE_DETECTION'];
                        break;
                }

                if (this.checkProperty(response, parsedProductType)) {
                    this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithResults);
                } else {
                    this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithWrongApp);
                }
            } else {
                this._globalSearchStatus$.next(GlobalSearchStatusEnum.doneWithResults);
            }
        }
    }

    checkProperty(response: TaqmanGlobalSearchResponseInterface, parsedProductTypes: string[]): boolean {
        let checked = false;
        if (response.productTypeHits && parsedProductTypes) {
            for (const value of parsedProductTypes) {
                if (Object.prototype.hasOwnProperty.call(response.productTypeHits, value) && (response.productTypeHits[value] > 0)) {
                    checked = true;
                    break;
                }
            }
        }
        return checked;
    }

    doSearch(): void {
        this.doSearchProducts();
        this.doSearchProductCounts();
    }

    setFilterPillState(filterIndex?: number, state?: boolean) {
        const totalFilters = this.getLocalFilters('filterState')?.length;

        if (totalFilters) {
            const currentState = Array(totalFilters).fill(false);
            currentState[filterIndex] = state;

            this.setLocalFilters({ overlayVisible: currentState });
        }
    }

    setLocalFilters(nextState: ITempFilters) {
        this._tempFilters$.next({ ...this._tempFilters$.getValue(), ...nextState });
    }

    getLocalFilters(key: string) {
        const selectedValue = this._tempFilters$.getValue()[key];
        return this._shallowCopy(selectedValue);
    }

    parseSuggestedKeywords(suggestedKeywords: string) {
        let parsed: string[] = [];
        if (suggestedKeywords && suggestedKeywords.length > 0) {
            parsed = suggestedKeywords.split(',');
        }
        return parsed;
    }

    handleLoadMore(): void {
        if (this.searchStatus$.getValue() === SearchStatusEnum.loadingMore) {
            return;
        }

        this.searchStatus$.next(SearchStatusEnum.loadingMore);

        const searchParams: SearchParamsInterface = this.searchParams$.getValue();
        const offset = this.searchResults$.getValue()?.products[searchParams.productSubtype]?.length?.toString() || '25';
        const taqmanSearchParams: TaqmanSearchRequestInterface = {
            keyword: searchParams.keyword,
            offset: offset,
            productType: searchParams.productType,
            productSubtype: searchParams.productSubtype,
            sortBy: searchParams.sortBy,
            filters: searchParams.filters,
            sortOrder: searchParams.sortOrder,
            filterState: searchParams.filterState
        }

        this.searchService.doSearch(taqmanSearchParams, searchParams.filterState).subscribe((response) => {

            const products = [...this.searchResults$.getValue().products[searchParams.productSubtype], ...response.products[searchParams.productSubtype]];

            this.searchResults$.next({
                ...this.searchResults$.getValue(),
                products: {
                    [searchParams.productSubtype]: products
                }
            });

            this.changeSearchParams({ offset: offset });

            this.searchStatus$.next(SearchStatusEnum.doneWithResults);

        });
    }

    loadMoreUntilProductLoaded(productId: string | null | undefined): void {
        if (!productId || this.deepLinkProductLoadInProgress || !this.isCurrentDeepLinkProductId(productId)) {
            return;
        }

        const searchParams: SearchParamsInterface = this.searchParams$.getValue();
        const searchResults = this.searchResults$.getValue();
        const productSubtype = searchParams.productSubtype;
        const products = searchResults?.products?.[productSubtype] || [];

        if (
            !searchResults ||
            products.some((product) => this.isSearchResultProductMatch(product, productId)) ||
            products.length >= searchResults.totalHits
        ) {
            return;
        }

        this.deepLinkProductLoadInProgress = true;
        this.searchStatus$.next(SearchStatusEnum.loadingMore);

        const offset = products.length.toString();
        const taqmanSearchParams: TaqmanSearchRequestInterface = {
            keyword: searchParams.keyword,
            offset: offset,
            limit: '100',
            productType: searchParams.productType,
            productSubtype: productSubtype,
            sortBy: searchParams.sortBy,
            filters: searchParams.filters,
            sortOrder: searchParams.sortOrder,
            filterState: searchParams.filterState,
        };

        this.searchService.doSearch(taqmanSearchParams, searchParams.filterState).subscribe({
            next: (response) => {
                const loadedProducts = response.products[productSubtype] || [];

                if (loadedProducts.length === 0) {
                    this.searchStatus$.next(SearchStatusEnum.doneWithResults);
                    this.deepLinkProductLoadInProgress = false;
                    return;
                }

                this.searchResults$.next({
                    ...searchResults,
                    products: {
                        ...searchResults.products,
                        [productSubtype]: [...products, ...loadedProducts],
                    },
                });

                this.changeSearchParams({ offset: offset });
                this.searchStatus$.next(SearchStatusEnum.doneWithResults);
                this.deepLinkProductLoadInProgress = false;

                if (this.isCurrentDeepLinkProductId(productId)) {
                    setTimeout(() => this.loadMoreUntilProductLoaded(productId));
                }
            },
            error: () => {
                this.searchStatus$.next(SearchStatusEnum.error);
                this.deepLinkProductLoadInProgress = false;
            },
        });
    }

    private isCurrentDeepLinkProductId(productId: string): boolean {
        if (typeof window === 'undefined') {
            return true;
        }

        const queryParams = new URLSearchParams(window.location.search);
        return (queryParams.get('rowId') || queryParams.get('rowid')) === productId;
    }

    handleFilterSearch(typeAheadKeyword: string, filterToSearch: string, filterIndex: number): void {
        const searchParams: SearchParamsInterface = this.searchParams$.getValue();
        const filters = this.searchResults$.getValue().filters;
        const filter = filters[filterIndex];
        const filterSearchParams: FilterSearchRequestInterface = {
            searchKeyword: searchParams.keyword,
            filterToSearch: filterToSearch,
            typeAheadKeyword: typeAheadKeyword.toUpperCase(),
            productType: searchParams.productType.toUpperCase(),
            productSubtype: searchParams.productSubtype.toUpperCase(),
        }

        this.apiService.filterSearch(filterSearchParams).subscribe(
            {
                next: (response) => {
                    this.processFilterSearch(response, filter, typeAheadKeyword, filters, filterIndex);
                },
                error: (err) => {
                    console.log('Error', err);
                    this.processFilterSearch(err, filter, typeAheadKeyword, filters, filterIndex);
                }
            }
        );
    }

    processFilterSearch(response: any, filter: any, typeAheadKeyword: string, filters: any, filterIndex: number) {
        const navigators = this.hasError(response) ? this.getNavigators() : response;
        const updated = {
            ...filter,
            navigators: navigators
        };
        const newFilters = [...filters.slice(0, filterIndex), updated, ...filters.slice(filterIndex + 1)];
        const geneFilterKeywords = Array(newFilters.length).fill('');
        geneFilterKeywords.fill(typeAheadKeyword, filterIndex, filterIndex + 1);
        const overLay = this.getLocalFilters('overlayVisible');
        overLay[filterIndex] = true;

        this.searchResults$.next({ ...this.searchResults$.getValue(), filters: newFilters });
        this.setLocalFilters({ geneFilterKeyword: geneFilterKeywords, overlayVisible: overLay });
        this.setFilterState(newFilters);
    }

    getNavigators() {
        const currentFilters = this.getLocalFilters('filters');
        let navigators = this._shallowCopy(this.initialGeneFilters['navigators']);

        // Only trigger if genes filter gets searched
        // The value that is filtered from the gene filter has to be added in the current filters
        for (let i = 0; i < currentFilters.length; i++) {
            if (currentFilters[i]['code'] === 'PrimaryGeneSymbolCaps') {
                navigators.unshift(currentFilters[i]['serverNavigator']);
                navigators = navigators.filter((value, index, self) => index === self.findIndex((t) => t.name === value.name));
                navigators.pop();
            }
        }

        return navigators;
    }

    hasError(response) {
        return Object.prototype.hasOwnProperty.call(response, 'details');
    }

    updateResultCount(params: ResultCountInterface) {
        if ((params.productType.toLowerCase() === 'ge' && params.productSubType.toLowerCase() === 'ge') ||
            (params.productType.toLowerCase() === 'snp' && params.productSubType.toLowerCase() === 'snp') ||
            (params.productType.toLowerCase() === 'cnv' && params.productSubType.toLowerCase() === 'cnv') ||
            (params.productType.toLowerCase() === 'microrna' && params.productSubType.toLowerCase() === 'microrna_primary') ||
            (params.productType.toLowerCase() === 'microrna' && params.productSubType.toLowerCase() === 'microrna_classic') ||
            (params.productType.toLowerCase() === 'microrna' && params.productSubType.toLowerCase() === 'microrna_advanced')) {
            this.setSearchResultCounts({ assayCount: params.count });
        } else {
            this.setSearchResultCounts({ arrayCount: params.count });
        }
    }

    setSearchResultCounts(nextState: SearchCountsInterface) {
        this._searchResultCountsState$.next({ ...this._searchResultCountsState$.getValue(), ...nextState });
    }

    setSearchParams(searchParams: Partial<SearchParamsInterface>): void {
        const nextSearchParams = { ...initialSearchParams, ...searchParams };

        if (nextSearchParams.filters) {
            nextSearchParams.filters = this.decodeIncomingFilters(nextSearchParams.filters);
            this.normalizeFiltersBrowserUrl(nextSearchParams.filters);

            const filterState = this.getLocalFilters('filterState');
            if (filterState?.length) {
                nextSearchParams.filters = this.decodeFiltersUrlParam(nextSearchParams.filters, filterState);
            }
        }

        this.searchParams$.next(nextSearchParams);
    }

    private decodeIncomingFilters(filters: string): string {
        const browserFilters = (filters || '').replace(/\+/g, ' ');

        try {
            return decodeURIComponent(browserFilters);
        } catch {
            return browserFilters;
        }
    }

    resetSearch(): void {
        this.deepLinkProductLoadInProgress = false;
        this.resetFilters();
        this.resetSearchResults();
        this.unselectAllProducts();
    }

    resetProductCountSearch(searchCountsParams: TaqmanProductCountRequestInterface): void {
        this.searchParams$.next({ ...initialProductCountParams, ...searchCountsParams });
        this._globalSearchResults$.next(null);
    }

    resetSearchResults(): void {
        this.searchResults$.next(null);
    }

    resetGlobalSearchResults(): void {
        this._globalSearchResults$.next(null);
        this._globalSearchStatus$.next(GlobalSearchStatusEnum.initial);
    }

    resetFilters(): void {
        this.setLocalFilters({ filters: [], filterState: [], selectedOptions: [] })
    }

    getKeyword(): string {
        return this.searchParams$.getValue().keyword;
    }

    setKeyword(newKeyword: string): void {
        this.searchParams$.next({ keyword: newKeyword });
    }

    selectAllProducts(): void {
        const productSubType = this.searchResults$.getValue().productSubType;
        const products = this.searchResults$.value.products[productSubType] ?? [];
        this.selectedResults$.next({ products: [...products] });
        this.allSelectedResults$.next(true);
    }

    unselectAllProducts(): void {
        this.selectedResults$.next({ selectedProducts: [], products: [] });
        this.allSelectedResults$.next(false);
    }

    addSelectedProduct(product: TaqmanProductInterface): void {
        const products = this.selectedResults$.value?.products || [];
        products.push(product);

        this.selectedResults$.next({ ...this.selectedResults$.getValue(), products: products });
        this.allSelectedResults$.next(false);
    }

    removeSelectedProduct(product: TaqmanProductInterface): void {
        const products = this.selectedResults$.value?.products || [];
        const index = products.findIndex((item) => item.productID == product.productID);
        if (index > -1) {
            products.splice(index, 1);
            this.selectedResults$.next({ ...this.selectedResults$.getValue(), products: products });
        }
        this.allSelectedResults$.next(false);
    }

    getSelectedProductsCount(): number {
        const products = this.selectedResults$.value?.products || [];
        return Object.keys(products).length;
    }

    getProductsCount(): number {
        const productTypeEnum = this.searchResults$.getValue().productTypeEnum;
        const products = this.searchResults$.value?.products[productTypeEnum] || [];
        return Object.keys(products).length;
    }

    getSelectedProductIds(): string[] {
        const products = this.selectedResults$.value?.products || [];
        return products.map(product => product.productID);
    }

    getSelectedAssayIds(): string[] {
        const products = this.selectedResults$.value?.products || [];
        return products.map(product => product.assayID);
    }

    getSelectedAssayIdsWithCatalogNumbers(): AssayCatalog[] {
        const products = this.selectedResults$.value?.products || [];
        return products.map(product => {
            const catalogNumber = product.selectedCatalogNumber ? product.selectedCatalogNumber : product.defaultCatalogNum;
            const dyeAndSizeInfoSelected = this.getDyeAndSizeOptionSelected(product, catalogNumber);
            return {
                'assayId': this.getProductType() === 'cnv' || this.getProductType() === 'ge' || this.getProductType() === 'microrna' ? product.productID : product.assayID,
                'catalogNumber': catalogNumber,
                'dye': dyeAndSizeInfoSelected?.dye || '',
                'size': dyeAndSizeInfoSelected?.size || '',
            }
        });

    }

    getDyeAndSizeOptionSelected (product, selectedCatalogNumber: string) {
        const dyeSizeSelected = {
            dye: '',
            size: ''
        }
        if(!product || !product.dyeSizeData || Array.isArray(product.dyeSizeData) || Object.keys(product.dyeSizeData)?.length === 0 || !selectedCatalogNumber){
            return dyeSizeSelected
        }
        const dyeSizes: string[] = Object.keys(product.dyeSizeData);
        for (const dye of dyeSizes) {
            const sizes = product?.dyeSizeData[dye] || [];
            for (const size of sizes) {
                if (size.catalogNumber === selectedCatalogNumber) {
                    dyeSizeSelected.dye = dye;
                    dyeSizeSelected.size = size.size;
                    return dyeSizeSelected;
                }
            }
        }
        return dyeSizeSelected
    }

    areAllResultsSelected(): boolean {
        return this.allSelectedResults$.getValue();
    }

    shouldDisplaySubtractIcon(): boolean {
        const selectedProductsCount = this.getSelectedProductsCount();
        return selectedProductsCount > 0 && this.getProductsCount() > selectedProductsCount;
    }

    shouldDisplayWarning(): boolean {

        if (this.limitWarning == this.getSelectedProductsCount()) {
            this.warningShown = true;
            return true;
        }

        return false;
    }

    shouldDisplayFirstWarning(): boolean {
        if (this.firstShown === true) {
            return false;
        }

        if (this.getSelectedProductsCount() === 1) {
            this.firstShown = true;
            return true;
        }

        return false;
    }

    getDyeOptions(dyeSizes: any): Dropdown[] {
        if (dyeSizes) {
            const dyes = Object.keys(dyeSizes);
            return dyes.map((dye) => makeDropdownOption(dye, dye, dye));
        }

        return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDyeOptionsGEAssays(dyeSizes: any): Dropdown[] {
        const dropdownOptions: Dropdown[] = [];
        const dyes = Object.keys(dyeSizes);

        const orderMap = new Map<string, number>();
        DYE_ORDER_CONFIG.GE_ASSAYS.forEach((aliases, index) => {
            aliases.forEach((dye) => orderMap.set(dye, index));
        });

        for (const dye of dyes) {
            if (Object.prototype.hasOwnProperty.call(dyeSizes, dye)) {
                const sizes = dyeSizes[dye];
                if (sizes.length > 0) {
                    // multiplexEligible is used to determine if the dye is enabled for GE assays
                    const availability = sizes[0].multiplexEligible === 'Y' ? 'multiplexEligible' : '';
                    const option = makeDropdownOption(dye, dye, availability);
                    dropdownOptions.push(option);
                }
            }
        }

        return dropdownOptions.sort((a, b) => {
            const indexA = orderMap.get(a.value) ?? Number.MAX_SAFE_INTEGER;
            const indexB = orderMap.get(b.value) ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
        });
    }

    getSizeOptions(dyeSizes: any, dye: string, _availability: string): Dropdown[] {
        if (dyeSizes && dye) {
            let sizes = dyeSizes[dye];
            sizes = sizes.map((size) => makeDropdownOption(size.catalogNumber, size.size, size.availability));
            sizes.sort((a, b) => {
                const numA = this.extractRxnsNumber(a.label || '');
                const numB = this.extractRxnsNumber(b.label || '');
                return numA - numB;
            });
            return sizes;
        }

        return null;
    }

    extractRxnsNumber(label: string): number {
        const match = label.match(/(\d+)\s*rxns/i);
        return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getSizeOptionsSNP(dyeSizes: any, dye: string, _availability: string): Dropdown[] {
        if (dyeSizes && dye) {
            let sizes = dyeSizes[dye];
            sizes = sizes.filter(sizeItem => sizeItem.displayOnSearch).map((size) => makeDropdownOption(size.catalogNumber, size.size, size.availability));
            if(Array.isArray(sizes) && sizes.length > 0){
                try{
                    sizes.sort((a:Dropdown, b:Dropdown):number => {
                        const firstSize:number = parseInt(a?.label?.split(': ')[1].replace(/,/g, '').split(' ')[0]);
                        const secondSize:number = parseInt(b?.label?.split(': ')[1].replace(/,/g, '').split(' ')[0]);
                        return firstSize - secondSize;
                    });
                }catch(error: unknown){
                    console.log(error);
                }
            }
            return sizes;
        }
        return null;
    }

    makeSizeOptions(dyeSizes: any, _availability: string): Dropdown[] {
        if (dyeSizes) {
            return dyeSizes.map((size) => makeDropdownOption(size.catalogNumber, size.size, size.availability));
        }

        return null;
    }

    _manipulateSelectedFilters(): Map<string, string> {
        const mappedFilters = new Map();
        const filters = this.getFilters();
        filters.forEach((filter: SelectItem) => {
            if (filter['app'] && filter['code']) {
                mappedFilters.set(filter['code'], `${mappedFilters.get(filter['code'])}${filter.value['name']},`);
            }
        })
        return mappedFilters;
    }

    _getFilteredString(newFilters: Map<string, string>): string {
        let strFilters = '';

        newFilters.forEach((value, key) => {
            value = value.substring(0, value.length - 1);
            value = value.replace('undefined', '');

            strFilters += `${key}:${value}~`;
        })

        return strFilters;
    }

    private decodeFiltersUrlParam(filters: string, filterState: ISearchFilters[] = this.getLocalFilters('filterState')): string {
        if (!filters.includes('~') && !filters.includes(':')) {
            const filterEntries = this.getBrowserFilterEntries(filters, filterState);
            const apiFilters = [];

            filterEntries.forEach(([name, values]) => {
                if (name && values) {
                    apiFilters.push(`${this.getApiFilterKey(name, filterState, values)}:${this.getApiFilterValues(name, values, filterState)}`);
                }
            });

            return apiFilters.length ? `${apiFilters.join('~')}~` : filters;
        }

        return filters
            .split('~')
            .map((filter) => {
                if (!filter) {
                    return filter;
                }

                const colonIndex = filter.indexOf(':');
                if (colonIndex > -1) {
                    const filterKey = filter.substring(0, colonIndex);
                    const values = filter.substring(colonIndex + 1);
                    return `${this.getApiFilterKey(filterKey, filterState, values)}:${this.getApiFilterValues(filterKey, values, filterState)}`;
                }

                const separatorIndex = filter.indexOf('/');
                if (separatorIndex === -1) {
                    return filter;
                }

                const filterKey = filter.substring(0, separatorIndex);
                const values = filter.substring(separatorIndex + 1);
                return `${this.getApiFilterKey(filterKey, filterState, values)}:${this.getApiFilterValues(filterKey, values, filterState)}`;
            })
            .join('~');
    }

    private getBrowserFilterEntries(filters: string, filterState: ISearchFilters[] = []): Array<[string, string]> {
        const filterParts = (filters || '').split('/');
        const filterEntries: Array<[string, string]> = [];

        for (let index = 0; index < filterParts.length;) {
            const filterName = filterParts[index++];
            const values = [];

            while (index < filterParts.length && !this.isBrowserFilterKey(filterParts[index], filterState)) {
                values.push(filterParts[index++]);
            }

            if (filterName && values.length) {
                filterEntries.push([filterName, values.join('/')]);
            }
        }

        return filterEntries;
    }

    private isBrowserFilterKey(filterKey: string, filterState: ISearchFilters[] = []): boolean {
        const normalizedFilterKey = this.normalizeBrowserFilterKey(filterKey);

        return filterState?.some((filter) => {
            return [
                filter.name,
                filter.urlFilter,
                filter.queryName,
                filter.solrQueryName,
            ].some((key) => this.normalizeBrowserFilterKey(key) === normalizedFilterKey);
        });
    }

    private checkRemoveCrossReactivityNavigator(filters: string, filterState: ISearchFilters[] = []): void {
        const selectedValues = this.getRemoveCrossReactivityValues(filters);

        if (!selectedValues.length) {
            return;
        }

        const selectedFilterValues = selectedValues.map((value) => this.normalizeRemoveCrossReactivityValue(value));
        const removeCrossReactivityFilter = filterState.find((filter) => {
            return this.normalizeBrowserFilterKey(filter.name) === 'remove cross-reactivity';
        });

        removeCrossReactivityFilter?.navigators?.forEach((navigator: INavigator) => {
            const navigatorName = this.normalizeRemoveCrossReactivityValue(navigator.name);
            navigator.checked = selectedFilterValues.some((value) => navigatorName === value);
        });
    }

    private getRemoveCrossReactivityValues(filters: string): string[] {
        return (filters || '').split('~').flatMap((filter) => {
            const separatorIndex = filter.indexOf('/') > -1 ? filter.indexOf('/') : filter.indexOf(':');

            if (separatorIndex === -1) {
                return [];
            }

            const filterName = filter.substring(0, separatorIndex);

            if (this.normalizeBrowserFilterKey(filterName) !== 'remove cross-reactivity') {
                return [];
            }

            return filter.substring(separatorIndex + 1).split(',').filter((value) => value);
        });
    }

    private normalizeRemoveCrossReactivityValue(value: string): string {
        const browserValue = (value || '').replace(/\+/g, ' ');

        try {
            return this.normalizeBrowserFilterKey(this.getBrowserFilterDisplayValue(decodeURIComponent(browserValue)));
        } catch {
            return this.normalizeBrowserFilterKey(this.getBrowserFilterDisplayValue(browserValue));
        }
    }

    private normalizeFiltersBrowserUrl(filters: string): void {
        if (typeof window === 'undefined') {
            return;
        }

        // ActivatedRoute emits while Angular is still completing the initial
        // navigation. Replacing the URL synchronously here can be overwritten
        // by the router committing the encoded wizard URL. Run the canonical
        // rewrite on the next task using the final browser query string.
        setTimeout(() => {
            const currentUrl = `${window.location.pathname}${window.location.search}`;
            const queryParams = new URLSearchParams(window.location.search);
            const currentFilters = queryParams.get('filters');

            if (!currentFilters) {
                return;
            }

        // Preserve the canonical array-search filter syntax supplied by
        // the TaqMan selection wizard.
        if (currentFilters.startsWith('species/') && currentFilters.includes('/array format/')) {
            return;
        }

        queryParams.set('filters', this.encodeFiltersBrowserParam(filters));

        const queryString = getBrowserQueryString(queryParams);
        const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

            if (nextUrl !== currentUrl) {
                replaceBrowserUrlState(nextUrl);
            }
        }, 0);
    }

    private encodeFiltersBrowserParam(filters: string): string {
        if (!filters.includes('~') && !filters.includes(':')) {
            return filters;
        }

        return filters
            .split('~')
            .filter((filter) => filter)
            .map((filter) => {
                const separatorIndex = filter.indexOf(':');

                if (separatorIndex === -1) {
                    return filter;
                }

                const filterKey = filter.substring(0, separatorIndex);
                const values = filter.substring(separatorIndex + 1);

                return `${this.getBrowserFilterKey(filterKey)}/${this.encodeBrowserFilterValues(values)}`;
            })
            .join('/');
    }

    private encodeBrowserFilterValues(values: string): string {
        return values
            .split(',')
            .map((value) => value.replace(/\//g, '%2F'))
            .join(',');
    }

    private getApiFilterKey(filterKey: string, filterState: ISearchFilters[] = [], values = ''): string {
        const matchedFilter = this.getMatchedFilter(filterKey, filterState);

        if (this.normalizeBrowserFilterKey(matchedFilter?.name) === 'genes') {
            const matchedNavigator = this.getMatchedNavigator(matchedFilter, values);

            if (matchedNavigator?.solrFacetName) {
                return matchedNavigator.solrFacetName;
            }
        }

        return matchedFilter?.queryName || filterKey;
    }

    private getApiFilterValues(filterKey: string, values: string, filterState: ISearchFilters[] = []): string {
        const matchedFilter = this.getMatchedFilter(filterKey, filterState);

        if (!matchedFilter?.navigators?.length) {
            return values;
        }

        return values
            .split(',')
            .map((value) => {
                const normalizedValue = this.normalizeBrowserFilterValue(value);
                const matchedNavigator = this.getMatchedNavigator(matchedFilter, value);

                return matchedNavigator?.queryValue || value;
            })
            .join(',');
    }

    private getMatchedNavigator(matchedFilter: ISearchFilters | undefined, values: string): INavigator | undefined {
        if (!matchedFilter?.navigators?.length) {
            return undefined;
        }

        const normalizedValue = this.normalizeBrowserFilterValue(values.split(',')[0]);

        return matchedFilter.navigators.find((navigator) => {
            return [
                navigator.name,
                this.getBrowserFilterDisplayValue(navigator.name),
                navigator.navigatorName,
                navigator.queryValue,
            ].some((navigatorValue) => this.normalizeBrowserFilterKey(navigatorValue) === normalizedValue);
        });
    }

    private getMatchedFilter(filterKey: string, filterState: ISearchFilters[] = []): ISearchFilters | undefined {
        const normalizedFilterKey = this.normalizeBrowserFilterKey(filterKey);
        return filterState?.find((filter) => {
            return [
                filter.queryName,
                filter.solrQueryName,
                filter.urlFilter,
                filter.name,
            ].some((key) => this.normalizeBrowserFilterKey(key) === normalizedFilterKey);
        });
    }

    private normalizeBrowserFilterValue(value: string): string {
        const browserValue = (value || '').replace(/\+/g, ' ');

        try {
            return this.normalizeBrowserFilterKey(decodeURIComponent(browserValue));
        } catch {
            return this.normalizeBrowserFilterKey(browserValue);
        }
    }

    private getBrowserFilterDisplayValue(value: string): string {
        return (value || '').replace(/\s*\[[^\]]*]\s*/g, ' ').trim();
    }

    private getBrowserFilterKey(filterKey: string): string {
        const filterState = this.getLocalFilters('filterState');
        const matchedFilter = filterState?.find((filter) => {
            return filter.queryName === filterKey || filter.solrQueryName === filterKey;
        });

        return (matchedFilter?.name || matchedFilter?.urlFilter || filterKey || '').toLowerCase();
    }

    private normalizeBrowserFilterKey(filterKey: string): string {
        return (filterKey || '').replace(/\+/g, ' ').trim().toLowerCase();
    }

    getFilters(): SelectItem[] {
        return this._tempFilters$.value.filters;
    }

    setModalFilters(nextState: any[], totalCount: number) {
        this._modalFilters$.next({
            ...this._modalFilters$.getValue(),
            filters: nextState,
            totalResultsCount: totalCount
        });
    }

    getProductType() {
        return this.searchParams$.getValue().productType;
    }

    selectCatalogNumber(product: GEProductInterface | GXProductInterface | SNPProductInterface, selectedCatalogNumber: string): void {
        let products;
        products = this.searchResults$.value?.products[product.productType == 'GT' ? ProductTypeEnum.snp : ProductTypeEnum.ge] || [];
        const index = products.findIndex((item) => item.productID == product.productID);
        if (index > -1) {
            products[index].selectedCatalogNumber = selectedCatalogNumber;
            const searchResults = this.searchResults$.getValue();
            products = this.searchResults$.value?.products[product.productType == 'GT' ? ProductTypeEnum.snp : ProductTypeEnum.ge] || [];
            searchResults.products[product.productType == 'GT' ? ProductTypeEnum.snp : ProductTypeEnum.ge] = [...products];
            this.searchResults$.next({ ...searchResults });
        }

        const selectedProducts = this.selectedResults$.value?.products || [];
        const selectedIndex = selectedProducts.findIndex((item) => item.productID == product.productID);
        if (selectedIndex > -1) {
            selectedProducts[index].selectedCatalogNumber = selectedCatalogNumber;
            this.selectedResults$.next({ ...this.selectedResults$.getValue(), products: selectedProducts });
        }
    }

    selectSize(product: GEProductInterface | SNPProductInterface, selectedSize: string): void {
        const products = this.searchResults$.value?.products[ProductTypeEnum.ge] || [];
        const index = products.findIndex((item) => item.productID == product.productID);
        if (index > -1) {
            products[index].selectedSize = selectedSize;
            const searchResults = this.searchResults$.getValue();
            searchResults.products[ProductTypeEnum.ge] = [...products];
            this.searchResults$.next({ ...searchResults });
        }
    }

    updateQuantity(product: GXProductInterface | MicrornaArrayProductInterface, qty: number, type: ProductTypeEnum | ProductSubtypeEnum): void {
        const products: GXProductInterface[] | MicrornaArrayProductInterface[] = this.searchResults$.value?.products[type] || [];
        const index = products.findIndex((item) => item.productID == product.productID);
        if (index > -1) {
            products[index].quantity = qty;
            const searchResults = this.searchResults$.getValue();
            searchResults.products[type] = [...products];
            this.searchResults$.next({ ...searchResults });
        }

        const selectedProducts = this.selectedResults$.value?.products || [];
        const selectedIndex = selectedProducts.findIndex((item) => item.productID == product.productID);
        if (selectedIndex > -1) {
            selectedProducts[index].quantity = qty;
            this.selectedResults$.next({ ...this.selectedResults$.getValue(), products: selectedProducts });
        }
    }

    _shallowCopy(objOrArray: any) {
        return JSON.parse(JSON.stringify(objOrArray));
    }

    notifyChanges(): void {
        this.searchResults$.next(this.searchResults$.getValue());
    }

    getLoadedProductsCount(): number {
        return this.searchResults$.getValue()?.products?.[this.searchParams$.getValue()?.productSubtype]?.length || 0;
    }

    private isSearchResultProductMatch(product: any, productId: string): boolean {
        return [
            product?.productID,
            product?.assayID,
            product?.defaultCatalogNum,
            product?.selectedCatalogNumber,
            product?.catalogNumber,
            product?.webID,
            product?.packageId,
            product?.partNumber,
            product?.taqmanProductID,
        ].some((id) => String(id || '') === productId);
    }

}
